import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import "./SurveysMain.css";

const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: 'rgba(87, 42, 241, 1)',
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#fff',
        border: '6px solid rgba(87, 42, 241, 0.3)',
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
      backgroundColor: '#fff',
    },
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: theme.palette.mode === 'dark' ? '#39393D' : '#E9E9EA',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  }));

const SurveysMain = () => {
  const [activeContent, setActiveContent] = useState("Survey Branding");
  const [npsFrequency, setNpsFrequency] = useState("Quarterly (recommended)");
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [cooldownEnabled, setCooldownEnabled] = useState(true);
  const [projectCompletionEnabled, setProjectCompletionEnabled] = useState(true);
  const [deliverableHandoffEnabled, setDeliverableHandoffEnabled] = useState(true);
  const [supportResolutionEnabled, setSupportResolutionEnabled] = useState(true);
  const [onboardingMilestoneEnabled, setOnboardingMilestoneEnabled] = useState(true);
  const [customContextEnabled, setCustomContextEnabled] = useState(true);
  const [customizationEnabled, setCustomizationEnabled] = useState(true);
  const [subjectLine, setSubjectLine] = useState("");
  const [introMessage, setIntroMessage] = useState("");
  const [closingMessage, setClosingMessage] = useState("");
  const [interactionType, setInteractionType] = useState("interaction");
  const [companyIntro, setCompanyIntro] = useState("");
const [formInstructions, setFormInstructions] = useState("");
const [completionMessage, setCompletionMessage] = useState("");
const [emailSubject, setEmailSubject] = useState("");
const [emailBody, setEmailBody] = useState("");

  const handleLinkClick = (e, content) => {
    e.preventDefault();
    setActiveContent(content);
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleFrequencyChange = (event) => {
    setNpsFrequency(event.target.value);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const renderContent = () => {
    switch (activeContent) {
      case "Survey Branding":
        return (
          <div>
            <h2 className="survey-centered-heading">Survey Branding</h2>
            <p>Upload your company logo to display on all survey communications (NPS, CES, and Onboarding surveys). This helps maintain consistent branding and increases survey recognition and completion rates.</p>
            
            <div className="file-requirements">
              <div>
                <h3>File Requirements:</h3>
                <ul>
                  <li>File formats: PNG or JPG</li>
                  <li>Dimensions: 250px × 100px (recommended)</li>
                  <li>Maximum file size: 2MB</li>
                </ul>
                
                <h3>Best Practices:</h3>
                <ul>
                  <li>Use a logo with adequate padding around the edges</li>
                  <li>Ensure your logo is clearly visible on both light and dark backgrounds</li>
                  <li>For best results, use your primary logo in full color</li>
                  <li>If your logo includes a tagline, consider using the simplified version without text</li>
                </ul>
              </div>
              
              <div className="file-upload-section">
                <div 
                  className={`upload-dropzone ${dragActive ? "drag-active" : ""}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <CloudUploadIcon className="upload-icon" />
                  <p>Drag and drop file here</p>
                </div>
                <Button
                  component="label"
                  variant="contained"
                  color="primary"
                  className="upload-button"
                >
                  Upload file
                  <VisuallyHiddenInput 
                    type="file" 
                    accept=".jpg,.jpeg,.png" 
                    onChange={handleFileChange} 
                  />
                </Button>
                {selectedFile && (
                  <p className="file-info">Selected file: {selectedFile.name}</p>
                )}
              </div>
            </div>
          </div>
        );
      case "NPS Survey Frequency":
        return (
          <div>
            <h2 className="survey-centered-heading">NPS Survey Frequency</h2>
            <p>Set how frequently ClientBloom should automatically send NPS surveys to your clients. This setting establishes a regular cadence for measuring client satisfaction and loyalty.</p>
            
            <div className="frequency-control">
              <div className="frequency-text">
                <p className="frequency-label">Send NPS surveys:</p>
                <ul className="frequency-options">
                  <li>Never (manual sending only)</li>
                  <li>Monthly</li>
                  <li>Quarterly (recommended)</li>
                  <li>Annually</li>
                </ul>
              </div>
              <div className="frequency-select-wrapper">
                <Select
                  value={npsFrequency}
                  onChange={handleFrequencyChange}
                  className="frequency-select"
                >
                  <MenuItem value="Never (manual sending only)">Never</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Quarterly (recommended)">Quarterly</MenuItem>
                  <MenuItem value="Annually">Annually</MenuItem>
                </Select>
              </div>
            </div>
            
            <h3>Survey Timing Explanation</h3>
            <p>When automated surveys are enabled, ClientBloom uses each client's onboarding date as the starting point for the survey schedule:</p>
            <ul>
              <li><strong>Monthly:</strong> Surveys sent on the same day each month as the client's onboarding date</li>
              <li><strong>Quarterly:</strong> Surveys sent every 3 months from the client's onboarding date</li>
              <li><strong>Annually:</strong> Surveys sent on the anniversary of the client's onboarding date</li>
            </ul>
            
            <p>For example, if a client was onboarded on March 15th and you select Quarterly frequency, they will receive NPS surveys on June 15th, September 15th, December 15th, and so on.</p>
            
            <h3>Important Notes:</h3>
            <ul>
              <li>This setting applies to automated surveys only. You can still manually send surveys at any time regardless of this setting.</li>
              <li>Clients will never receive duplicate surveys. If you manually send an NPS survey, the automated schedule will reset from that date.</li>
              <li>New clients will receive their first automated NPS survey after completing onboarding plus 30 days to allow sufficient time for service experience</li>
            </ul>
          </div>
        );
        case "CES Survey Frequency":
            return (
              <div>
                <h2 className="survey-centered-heading">Client Effort Score (CES) Surveys</h2>
                <p>These settings establish the default parameters for all Client Effort Score (CES) surveys sent from your ClientBloom account. CES surveys will be sent manually by CSMs from the client dashboard.</p>
                
                <h3>Follow Up Reminders:</h3>
                  <p className="indented-text">Send one reminder if survey not completed (recommended) A reminder message will be sent after 48 hours</p>
                <h3>Survey Cooldown Period:</h3>
                  <p className="indented-text">When toggled, this setting prevents duplicate surveys being sent to the same client too frequently and limits the period to no less than 14 days</p>
          
                <h3>Default Survey Context:</h3>
                <p>When sending a CES survey, CSMs will select what type of interaction prompted the survey. Set which options are available:</p>
                
                <div className="ios-switch-container">
                  <IOSSwitch
                    checked={projectCompletionEnabled}
                    onChange={(e) => setProjectCompletionEnabled(e.target.checked)}
                    name="projectCompletionEnabled"
                  />
                  <span>Project Completion</span>
                </div>
          
                <div className="ios-switch-container">
                  <IOSSwitch
                    checked={deliverableHandoffEnabled}
                    onChange={(e) => setDeliverableHandoffEnabled(e.target.checked)}
                    name="deliverableHandoffEnabled"
                  />
                  <span>Deliverable Handoff</span>
                </div>
          
                <div className="ios-switch-container">
                  <IOSSwitch
                    checked={supportResolutionEnabled}
                    onChange={(e) => setSupportResolutionEnabled(e.target.checked)}
                    name="supportResolutionEnabled"
                  />
                  <span>Support Resolution</span>
                </div>
          
                <div className="ios-switch-container">
                  <IOSSwitch
                    checked={onboardingMilestoneEnabled}
                    onChange={(e) => setOnboardingMilestoneEnabled(e.target.checked)}
                    name="onboardingMilestoneEnabled"
                  />
                  <span>Onboarding Milestone</span>
                </div>
          
                <div className="ios-switch-container">
                  <IOSSwitch
                    checked={customContextEnabled}
                    onChange={(e) => setCustomContextEnabled(e.target.checked)}
                    name="customContextEnabled"
                  />
                  <span>Custom context (allow CSMs to enter custom reason)</span>
                </div>
          
                <h3>CSM Customization Permission:</h3>
                <div className="ios-switch-container">
                  <IOSSwitch
                    checked={customizationEnabled}
                    onChange={(e) => setCustomizationEnabled(e.target.checked)}
                    name="customizationEnabled"
                  />
                  <span>Allow CSMs to customize messages when sending surveys</span>
                </div>
                <p>When enabled, CSMs can modify subject lines and messages for individual surveys. When disabled, all surveys use your global templates for consistency.</p>
          
                <h3>Variable Tags Available:</h3>
                <ul className="no-numbers-list">
                  <li><span className="variable-tag">{'{company_name}'}</span> - Your company name</li>
                  <li><span className="variable-tag">{'{client_name}'}</span> - Client's name</li>
                  <li><span className="variable-tag">{'{interaction_type}'}</span> - Type of interaction (project, deliverable, etc.)</li>
                  <li><span className="variable-tag">{'{csm_name}'}</span> - Name of the client's CSM</li>
                </ul>
          
                <h3>Custom Message Template:</h3>
                <p>CES surveys are standardized to collect reliable, consistent feedback data. We recommend using our default templates for optimal results, but you can customize the messaging to align with your brand voice.</p>
                <p>Changes here apply to all CES surveys sent from your account.</p>
          
                <div className="template-section">
                  <h4>Email Subject Line</h4>
                  <p>Default: "How easy was your recent experience with us?"</p>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Enter Your Custom Text Here (leave this blank to use default)"
                    value={subjectLine}
                    onChange={(e) => setSubjectLine(e.target.value)}
                    inputProps={{ maxLength: 50 }}
                  />
                  <p className="character-count">Max 50 Characters</p>
                  <Button variant="contained" color="primary" className="save-button">
                    Save
                  </Button>
                </div>
          
                <div className="template-section">
                  <h4>Introduction Message</h4>
                  <p>Default: "We're committed to making our services as seamless as possible. Please rate how easy it was to work with us on your recent {'{interaction_type}'}."</p>
                  <TextField
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={4}
                    placeholder="Enter Your Custom Text Here (leave this blank to use default)"
                    value={introMessage}
                    onChange={(e) => setIntroMessage(e.target.value)}
                    inputProps={{ maxLength: 300 }}
                  />
                  <p className="character-count">Max 300 Characters</p>
                  <Button variant="contained" color="primary" className="save-button">
                    Save
                  </Button>
                </div>
          
                <div className="template-section">
                  <h4>Closing Message</h4>
                  <p>Default: "Thank you for your feedback. Your input helps us continuously improve our client experience."</p>
                  <TextField
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    placeholder="Enter Your Custom Text Here (leave this blank to use default)"
                    value={closingMessage}
                    onChange={(e) => setClosingMessage(e.target.value)}
                    inputProps={{ maxLength: 250 }}
                  />
                  <p className="character-count">Max 250 Characters</p>
                  <Button variant="contained" color="primary" className="save-button">
                    Save
                  </Button>
                </div>
              </div>
            );
            case "Onboarding Surveys":
  return (
    <div>
      <h2 className="survey-centered-heading">Onboarding Surveys</h2>

      <p>
        The Onboarding Survey Form includes essential questions suitable for most businesses. This standardized form helps you collect crucial client information while minimizing setup time. All questions are pre-configured to gather the most important information for successful client relationships.
      </p>
      <p>
        If you plan to include onboarding survey completion in your CRS calculations, we recommend using the default message settings provided below. These defaults have been optimized for high completion rates. However, you can customize messages to better align with your brand voice and client communication style.
      </p>

      <h3>Form Customization:</h3>

      {/* Company Introduction */}
      <div className="template-section">
        <h4>Company Introduction</h4>
        <p>
          This appears at the top of your form, introducing your business to new clients.
        </p>
        <p>
          Default: Welcome to {'{company_name}'}! We're excited to begin our work together. This brief onboarding form will help us gather the information needed to ensure your success as a client.
        </p>
        <TextField
          fullWidth
          variant="outlined"
          multiline
          rows={4}
          placeholder="Enter Your Custom Text Here (leave this blank to use default)"
          value={companyIntro}
          onChange={(e) => setCompanyIntro(e.target.value)}
          inputProps={{ maxLength: 400 }}
        />
        <p className="character-count">Max 400 Characters</p>
        <Button variant="contained" color="primary" className="save-button">
          Save
        </Button>
      </div>

      {/* Form Instructions */}
      <div className="template-section">
        <h4>Form Instructions</h4>
        <p>
          Provide clear directions on how to complete the form and what to expect.
        </p>
        <p>
          Default: Please answer the following questions to help us better understand your business and needs. This information will be used by your dedicated client success manager to provide the best possible service.
        </p>
        <TextField
          fullWidth
          variant="outlined"
          multiline
          rows={4}
          placeholder="Enter Your Custom Text Here (leave this blank to use default)"
          value={formInstructions}
          onChange={(e) => setFormInstructions(e.target.value)}
          inputProps={{ maxLength: 400 }}
        />
        <p className="character-count">Max 400 Characters</p>
        <Button variant="contained" color="primary" className="save-button">
          Save
        </Button>
      </div>

      {/* Completion Message */}
      <div className="template-section">
        <h4>Completion Message</h4>
        <p>
          This message appears after clients submit the completed form.
        </p>
        <p>
          Default: Thank you for completing your onboarding information! Your client success manager will review your responses and reach out promptly to discuss next steps.
        </p>
        <TextField
          fullWidth
          variant="outlined"
          multiline
          rows={3}
          placeholder="Enter Your Custom Text Here (leave this blank to use default)"
          value={completionMessage}
          onChange={(e) => setCompletionMessage(e.target.value)}
          inputProps={{ maxLength: 250 }}
        />
        <p className="character-count">Max 250 Characters</p>
        <Button variant="contained" color="primary" className="save-button">
          Save
        </Button>
      </div>

      {/* Standard Questions */}
      <h3>Standard Questions (All Included)</h3>
      <p>
        The Onboarding Survey Form includes these pre-built questions:
      </p>
      <ol className="standard-questions">
        <li>
          <strong>Business Overview:</strong> "Please provide a brief description of your business and its main objectives."
        </li>
        <li>
          <strong>Project Goals:</strong> "What specific goals are you hoping to achieve through our work together?"
        </li>
        <li>
          <strong>Key Stakeholders:</strong> "Who will be the primary contacts for this relationship? Please include names, roles, and contact information."
        </li>
        <li>
          <strong>Communication Preferences:</strong> "What is your preferred method and frequency of communication?"
          <ul>
            <li><strong>Options:</strong> Email, Phone, Video Call, Text, In-person</li>
            <li><strong>Frequency:</strong> Weekly, Bi-weekly, Monthly, As needed</li>
          </ul>
        </li>
        <li>
          <strong>Availability:</strong> "What days/times work best for regular meetings or check-ins?"
        </li>
        <li>
          <strong>Systems Access:</strong> "What systems or platforms will we need access to? Please include any login information or instructions for requesting access."
        </li>
        <li>
          <strong>Success Metrics:</strong> "How will you measure the success of our work together?"
        </li>
        <li>
          <strong>Previous Experience:</strong> "Have you worked with a similar service provider before? If so, what worked well and what could have been improved?"
        </li>
        <li>
          <strong>Timeline Expectations:</strong> "Do you have any specific timeline expectations or deadlines we should be aware of?"
        </li>
        <li>
          <strong>Additional Information:</strong> "Is there anything else you'd like us to know to better serve you?"
        </li>
      </ol>

      {/* Plantilla de Correo Electrónico */}
      <h3>Email Template</h3>
      <p>
        This email delivers the Onboarding Survey link to your clients. It serves as their first formal interaction with your onboarding process, so the messaging significantly impacts completion rates. We suggest leaving these messages in their default state, but you can customize them if you prefer.
      </p>

      <div className="template-section">
        <h4>Email Subject Line</h4>
        <p>
          Default: Welcome to {'{company_name}'}: Your Onboarding Form
        </p>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Enter Your Custom Text Here (leave this blank to use default)"
          value={emailSubject}
          onChange={(e) => setEmailSubject(e.target.value)}
          inputProps={{ maxLength: 60 }}
        />
        <p className="character-count">Max 60 Characters</p>
        <Button variant="contained" color="primary" className="save-button">
          Save
        </Button>
      </div>

      <div className="template-section">
        <h4>Email Body</h4>
        <p>
          Default:
        </p>
        <div className="email-template">
          <p>Hello {'{client_name}'},</p>
          <p>Welcome to {'{company_name}'}! We're excited to have you on board.</p>
          <p>
            To help us serve you better, please complete this brief onboarding form. The information you provide will help us understand your needs and ensure a smooth working relationship.
          </p>
          <p>[FORM LINK]</p>
          <p>
            Please complete this form within the next 3 business days. If you have any questions, don't hesitate to reach out to your client success manager, {'{csm_name}'}, at {'{csm_email}'}.
          </p>
          <p>We look forward to our partnership!</p>
          <p>Best regards,<br/>{'{company_name}'} Team</p>
        </div>
        <TextField
          fullWidth
          variant="outlined"
          multiline
          rows={8}
          placeholder="Enter Your Custom Text Here (leave this blank to use default)"
          value={emailBody}
          onChange={(e) => setEmailBody(e.target.value)}
          inputProps={{ maxLength: 700 }}
        />
        <p className="character-count">Max 700 Characters</p>
        <Button variant="contained" color="primary" className="save-button">
          Save
        </Button>
      </div>
    </div>
  );
      default:
        return <div className="empty-state">Select a survey setting to view details</div>;
    }
  };

  return (
    <>
      <div className="bannerSoon">This feature will be available soon.</div>
    <div className="survey-main-container">
      <div className="survey-left-panel">
        <h1>Survey Settings</h1>
        <p>
          The settings in this section apply globally to all surveys across your
          ClientBloom account.
        </p>
        <p>
          These configurations establish the default behavior for all surveys and
          cannot be overridden by individual CSMs. Any changes made here will
          affect all future surveys sent from your account.
        </p>
        <p>
          These global settings ensure consistency in your client feedback process
          while still allowing flexibility when sending individual surveys through
          the Surveys page in the main navigation.
        </p>

        <h1 className="settings-subtitle">Customize Survey Settings</h1>
        <p>
          Configure your global survey settings below. These will apply to all surveys sent from your account.
        </p>
        <h2>Survey Configuration</h2>
        <ul className="survey-metric-list">
        <li>
            <a 
            href="#" 
            onClick={(e) => handleLinkClick(e, "Survey Branding")} 
            className={activeContent === "Survey Branding" ? "active" : ""}
            >
            Survey Branding
            </a>
        </li>
        <li>
            <a 
            href="#" 
            onClick={(e) => handleLinkClick(e, "NPS Survey Frequency")} 
            className={activeContent === "NPS Survey Frequency" ? "active" : ""}
            >
            NPS Survey Frequency
            </a>
        </li>
        <li>
            <a 
            href="#" 
            onClick={(e) => handleLinkClick(e, "CES Survey Frequency")} 
            className={activeContent === "CES Survey Frequency" ? "active" : ""}
            >
            CES Survey Frequency
            </a>
        </li>
        <li>
            <a 
            href="#" 
            onClick={(e) => handleLinkClick(e, "Onboarding Surveys")} 
            className={activeContent === "Onboarding Surveys" ? "active" : ""}
            >
            Onboarding Surveys
            </a>
        </li>
        </ul>
      </div>

      <div className="survey-right-panel">
        {renderContent()}
      </div>
    </div>
    </>
  );
};

export default SurveysMain;